import { Link, useParams } from "react-router-dom";
import { useGitOpsApplication, useGitOpsApplications, useScope } from "@/api/queries";
import { HttpError } from "@/api/client";
import { useDashboardStore } from "@/state/dashboardStore";
import { useDashboardParams } from "@/state/useDashboardParams";
import { DegradedState, EmptyState, ErrorState, ForbiddenState, LoadingState } from "@/components/SectionState";
import { Panel } from "@/components/primitives";

const title = "GitOps Applications";

export function GitOpsApplicationsView() {
  const { uid = "" } = useParams();
  const { clusterId } = useDashboardParams();
  const scope = useScope();
  const ready = useDashboardStore((s) => s.namespaceSelectionStatus) === "ready";
  const enabled = Boolean(scope.data && !scope.isFetching && !scope.isError && ready && scope.data.canViewGitOps);
  const list = useGitOpsApplications(clusterId, enabled && !uid);
  const detail = useGitOpsApplication(clusterId, uid, enabled && Boolean(uid));
  if (!scope.data && scope.isLoading) return <LoadingState />;
  if (!enabled) {
    const reason = !ready || scope.isFetching || scope.isError ? "scope_stale" : "disabled";
    return <Panel title={title}><DegradedState detail={reason} /></Panel>;
  }
  const query = uid ? detail : list;
  if (query.isLoading) return <LoadingState />;
  if (query.isError || !query.data) {
    const sourceForbidden = query.error instanceof HttpError && query.error.status === 403;
    return <Panel title={title}>{sourceForbidden
      ? <ForbiddenState />
      : <ErrorState detail={uid ? "Application을 찾을 수 없거나 조회할 수 없습니다." : "GitOps 정보를 불러오지 못했습니다."} />}</Panel>;
  }
  const section = uid ? detail.data?.application : list.data?.applications;
  if (!section || section.status !== "ok" || !section.data) return <Panel title={title}>{section?.status === "forbidden" ? <ForbiddenState /> : section?.status === "degraded" ? <DegradedState source={section.source} detail={section.reason} /> : <EmptyState title="Application이 없습니다" />}</Panel>;
  if (uid && "uid" in section.data) {
    const item = section.data;
    return <Panel title={item.name} subtitle={`UID ${item.uid}`}><p>{item.sourceNamespace} · {item.destinationNamespace} · {item.project ?? "-"}</p><p>{item.syncStatus} · {item.healthStatus} · {item.reconciledAt ?? "-"}</p>{item.conditions?.length ? <ul aria-label="Conditions">{item.conditions.map((c) => <li key={c.type}>{c.type} · {c.lastTransitionAt}</li>)}</ul> : null}</Panel>;
  }
  const page = section.data as { items: Array<{ uid:string;name:string;destinationNamespace:string;syncStatus:string;healthStatus:string }> };
  return <Panel title={title}><ul>{page.items.map((item) => <li key={item.uid}><Link to={`/gitops/applications/${encodeURIComponent(item.uid)}`}>{item.name}</Link> · {item.destinationNamespace} · <span>{item.syncStatus}</span> · <span>{item.healthStatus}</span></li>)}</ul></Panel>;
}
